// voice-call-new.service.ts (UPDATED)

import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { VoiceService, SipService } from '@dxp/core/services';
import { AgentApiService, VoiceApiService } from '@dxp/core/api';
import {
    ICostCentre,
    IInternalHistoricalWorkItems,
    IUser,
    IVoiceOutgoing,
} from '@dxp/core/models';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, switchMap } from 'rxjs';
import { USER_STORE, WORKITEMS_STORE } from '@dxp/core/stores';
import { VoiceStore } from '../store/voice.store';

@Injectable({ providedIn: 'root' })
export class VoiceCallNewService {
    readonly #workItemStore = inject(WORKITEMS_STORE);
    readonly #voiceApiService = inject(VoiceApiService);
    readonly #voiceService = inject(VoiceService);
    readonly #sipService = inject(SipService);
    readonly #userStore = inject(USER_STORE);
    readonly #agentApiService = inject(AgentApiService);
    readonly #voiceStore = inject(VoiceStore);

    readonly callNumber = signal('');
    readonly dialpad = signal(false);
    readonly costCentreId = signal('');
    readonly overrideCLI = signal<string | null>(null);
    readonly overrideWithheld = signal(false);
    readonly withholdCLI = signal(false);
    readonly startedCall = signal(false);
    readonly agentUnavailable = signal(false);
    readonly disableCostCentersForInternalCall = signal(false);

    conversationId = '';

    constructor() {
        this.user = this.#userStore.user();

        this.recentCalls = toSignal(
            this.refreshTrigger$.pipe(
                switchMap(() =>
                    this.#agentApiService.getWorkitemHistoryByAgent(
                        this.user.id,
                        true,
                        ['Conversations'],
                        0,
                        20,
                        { channelTypes: ['voice'] },
                        'startTimeUtc desc',
                    ),
                ),
            ),
            { initialValue: null },
        );

        this.costCentres = toSignal(
            this.refreshTrigger$.pipe(
                switchMap(() => this.#voiceApiService.getCostCentres()),
            ),
            { initialValue: null },
        );
    }

    readonly costCentres: Signal<ICostCentre[] | null>;
    readonly recentCalls!: Signal<IInternalHistoricalWorkItems | null>;
    readonly user: IUser;

    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

    readonly redialBlocked = signal(false);

    readonly contactDetails = computed(
        () => this.#voiceService.selectedContact()?.identifier ?? null,
    );

    readonly baseCLI = computed(
        () => this.#voiceApiService.operatorCLI() ?? null,
    );

    readonly opCLI = computed(() => this.overrideCLI() ?? this.baseCLI());

    readonly canCall = computed(
        () =>
            this.callNumber() !== '' &&
            this.costCentreId() !== '' &&
            !this.startedCall() &&
            !this.redialBlocked(),
    );

    readonly callToolTip = computed(() =>
        this.connecting()
            ? ''
            : this.startedCall()
              ? 'End Call'
              : this.canCall()
                ? 'Make call'
                : this.costCentreId() === ''
                  ? 'Please select a channel'
                  : 'No destination',
    );

    private previousCallStateValue = '';
    private previousCallStateReasonValue = '';
    private previousCallStateChanged = signal(false);

    readonly callState = computed(() => {
        if (this.agentUnavailable()) return 'Agent unavailable';

        this.previousCallStateChanged();

        const outboundItem = this.#workItemStore
            .workItems()
            .find(wi => wi.isOutbound);

        const conversation = outboundItem?.conversations?.find(
            c => c.conversationId === outboundItem.primaryConversationId,
        );

        this.conversationId = outboundItem?.primaryConversationId ?? '';

        let state = conversation?.outboundCallState;

        if (state) {
            this.previousCallStateValue = state;
            this.previousCallStateReasonValue =
                conversation?.outboundCallStateReason ?? '';
        } else {
            state = this.previousCallStateValue;
        }
        switch (state) {
            case 'Rejected':
                return 'Agent rejected';
            case 'Ringing':
                return 'Ringing...';
            case 'Failed':
                return this.previousCallStateReasonValue ===
                    'Call to target failed: Engaged'
                    ? 'Number busy'
                    : 'Call failed';
            default:
                return this.startedCall() ? 'Connecting...' : '';
        }
    });

    readonly displayFailed = computed(() => {
        const s = this.callState();
        return (
            s === 'Call failed' ||
            s === 'Number busy' ||
            s === 'Agent rejected' ||
            s === 'Agent unavailable'
        );
    });

    readonly connecting = computed(() =>
        this.callState().includes('Connecting...'),
    );

    readonly costCentresData = computed(() => {
        const centres = this.costCentres();
        if (!centres) return [];
        return centres.map((centre: ICostCentre) => ({
            label: centre?.costCentreName,
            value: centre?.costCentreId?.toString?.() ?? '',
            disabled: false,
        }));
    });

    selectedContact = computed(() => this.#voiceService.selectedContact());

    refreshData(): void {
        this.refreshTrigger$.next();
        this.getOperatorCLI();
    }

    getOperatorCLI() {
        return this.#voiceApiService.getOperatorCLI();
    }

    setWithholdCLI(checked: boolean) {
        this.withholdCLI.set(checked);
    }

    updateNumber(value: string) {
        this.#voiceService.selectedContact.set(null);
        this.callNumber.set(value);
    }

    makeCall(type?: string) {
        this.#sipService.setAutoAnswer();

        let destination = this.#voiceService.selectedContact()
            ? `${this.#voiceService.selectedContact()?.identifier}`
            : this.callNumber();

        if (type === 'agent') {
            destination = `agent${destination}`;
        }

        const params: IVoiceOutgoing = {
            agentId: this.user.id,
            aCliOption: '0',
            bCliOption: '0',
            costCentreId: Number(this.costCentreId() ?? 0),
            withheld: this.withholdCLI(),
            destination: destination,
            allowPayment: this.#voiceStore.paymentsEnabled(),
        };

        this.agentUnavailable.set(false);
        this.startedCall.set(true);
        this.#voiceApiService.makeOutgoingCall(params).subscribe({
            error: (err: HttpErrorResponse) => {
                if (err.status === 409) {
                    this.agentUnavailable.set(true);
                    this.startedCall.set(false);
                }
            },
        });
        this.#voiceService.setContactCardSelected('');
        this.#voiceService.selectedContact.set(null);
    }

    endCall() {
        this.#sipService.hangUp();
        this.callNumber.set('');
        this.startedCall.set(false);
    }

    handleTerminalEnd() {
        this.startedCall.set(false);
    }

    clearAfterTerminal() {
        this.callNumber.set('');
        this.previousCallStateValue = '';
        this.previousCallStateReasonValue = '';
        this.previousCallStateChanged.set(!this.previousCallStateChanged());
        this.agentUnavailable.set(false);
        this.resetInternalCostCentre();
    }

    toggleDialpad() {
        this.dialpad.set(!this.dialpad());
    }

    dialpadClick(number: string) {
        if (this.callNumber().length < 15) {
            this.#voiceService.selectedContact.set(null);
            this.callNumber.set(`${this.callNumber()}${number}`);
        }
    }

    onCostCentreChange(costCentreId: string) {
        this.costCentreId.set(costCentreId);
        const costCentres = this.costCentres();
        const selectedCC = costCentres?.find(
            cc => cc.costCentreId.toString() === costCentreId,
        );
        console.log(selectedCC);
        this.overrideCLI.set(selectedCC?.cli?.trim() ? selectedCC.cli : null);
        this.overrideWithheld.set(selectedCC?.cliWithhold ?? false);
        this.withholdCLI.set(selectedCC?.cliWithhold ?? false);
    }

    internalCostCentreChange(): void {
        const internalCostCentre = this.costCentres()?.find(
            costCentre => costCentre.forInternal,
        );

        if (internalCostCentre) {
            this.onCostCentreChange(`${internalCostCentre.costCentreId}`);
            this.disableCostCentersForInternalCall.set(true);
        }
    }

    resetInternalCostCentre(): void {
        const internalCostCentre = this.costCentres()?.find(
            costCentre => costCentre.forInternal,
        );
        if (
            internalCostCentre &&
            this.costCentreId() === `${internalCostCentre.costCentreId}`
        ) {
            this.costCentreId.set('');
        }
        this.disableCostCentersForInternalCall.set(false);
    }

    reset() {
        this.previousCallStateValue = '';
        this.previousCallStateReasonValue = '';
        this.previousCallStateChanged.set(!this.previousCallStateChanged());
        this.redialBlocked.set(false);
        this.callNumber.set('');
        this.overrideWithheld.set(false);
        this.dialpad.set(false);
        this.costCentreId.set('');
        this.overrideWithheld.set(false);
        this.withholdCLI.set(false);
        this.startedCall.set(false);
        this.agentUnavailable.set(false);
        this.disableCostCentersForInternalCall.set(false);
    }
}
